// b17: Homey Pro i appen, bara läsning (status beta). Under Inställningar → Homey, och i guiden:
//
//   - Homeys adress på platsens nät, eller den Homey som navet hittar med mDNS.
//   - Nyckeln ur Homeys webbapp, med bara rätten att se enheter. Den förseglas här i webbläsaren
//     med navets publika nyckel, och bara lådan skickas: molnet kan inte läsa nyckeln.
//   - Kontakten, och en varning om Homey säger att nyckeln får mer än att läsa.

import {
  HOMEY_ADRESS,
  HOMEY_HJALP,
  HOMEY_MDNS,
  HOMEY_NYCKEL,
  type Homeyinstallningar,
  type Platsinstallningar,
} from "@batkoll/gemensam";
import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { api } from "../api.ts";
import { Kort } from "../komponenter.tsx";

const NYCKELTEXT: Record<string, string> = {
  ingen: "Navet har ingen nyckel för Homey.",
  skickad: "Nyckeln är på väg till navet, förseglad.",
  mottagen: "Navet har nyckeln, krypterad.",
};

/** Läget för Homey i ord: kontakten, nyckeln och behörigheterna. Aldrig nyckeln. */
export function homeylage(h: Homeyinstallningar): string[] {
  const ut = [NYCKELTEXT[h.nyckel] ?? ""];
  if (h.nyckelNavet === "fel") ut.push("Homey godtar inte nyckeln. Skapa en ny och skriv in den.");
  if (h.adress && h.kontakt === true) ut.push(`Navet har kontakt med Homey: ${h.enheter} enheter.`);
  if (h.adress && h.kontakt === false) {
    ut.push("Navet har ingen kontakt med Homey. Stämmer adressen?");
  }
  return ut.filter((x) => x !== "");
}

/** Varningen när nyckeln får mer än att läsa, eller null. */
export function homeyvarning(h: Homeyinstallningar): string | null {
  return h.behorighet === "mer_an_lasning"
    ? "Varning: nyckeln får mer än att se enheterna. Navet läser bara, men skapa gärna en ny nyckel med bara Visa enheter (devices.readonly), och ta bort den gamla i Homey."
    : null;
}

/** Homeys adress, med förslaget från mDNS. */
export function Homeyadressfalt(props: {
  adress: string;
  forslag: boolean;
  satt: (a: string) => void;
}) {
  const fel = props.adress.trim() !== "" && !HOMEY_ADRESS.test(props.adress.trim());
  return (
    <>
      <label>
        Homeys adress på platsens nät
        <input
          type="text"
          inputMode="url"
          autoComplete="off"
          placeholder="till exempel 192.168.1.50"
          value={props.adress === HOMEY_MDNS ? "" : props.adress}
          aria-invalid={fel}
          onInput={(e) => props.satt((e.target as HTMLInputElement).value)}
        />
      </label>
      {props.adress === HOMEY_MDNS && (
        <p class="liten">Navet läser den Homey som det hittar på platsens nät (mDNS).</p>
      )}
      {props.forslag && props.adress.trim() === "" && (
        <p class="liten">
          Navet hittar en Homey på platsens nät.{" "}
          <button type="button" class="knapp-lank" onClick={() => props.satt(HOMEY_MDNS)}>
            Använd den Homey som navet hittar
          </button>
        </p>
      )}
    </>
  );
}

/** Nyckeln för Homey. Den stannar i webbläsaren tills den förseglas. */
export function Homeynyckelfalt(props: { varde: string; satt: (v: string) => void }) {
  const fel = props.varde.trim() !== "" && !HOMEY_NYCKEL.test(props.varde.trim());
  return (
    <>
      <label>
        API-nyckeln för Homey
        <input
          type="password"
          autoComplete="off"
          spellcheck={false}
          maxLength={400}
          value={props.varde}
          aria-invalid={fel}
          onInput={(e) => props.satt((e.target as HTMLInputElement).value)}
        />
      </label>
      <p class="liten">{HOMEY_HJALP}</p>
    </>
  );
}

/** Inställningar → Homey. */
export function Homeydel(props: {
  s: Platsinstallningar;
  spara: (
    sektion: string,
    gor: () => Promise<Platsinstallningar | [Platsinstallningar, string]>,
    ok?: string,
  ) => Promise<boolean>;
  beskedrad: ComponentChildren;
}) {
  const h = props.s.homey;
  const plats = props.s.plats.id;
  const [adress, setAdress] = useState(h?.adress ?? "");
  const [nyckel, setNyckel] = useState("");
  if (!h) return null;
  const adressOk = adress.trim() === "" || HOMEY_ADRESS.test(adress.trim());
  const varning = homeyvarning(h);

  async function sparaNyckel() {
    if (!h?.lada) return;
    const ok = await props.spara(
      "homey",
      () => api.hemlighet(plats, "homey", h.lada as string, { nyckel: nyckel.trim() }),
      "Nyckeln för Homey förseglades och skickades till navet. Molnet kan inte läsa den.",
    );
    if (ok) setNyckel("");
  }

  return (
    <Kort rubrik="Homey" bred>
      <p class="liten">
        Homey Pro läses bara, via Homeys lokala API: navet hämtar enheterna och värdena, och ändrar
        aldrig något i Homey. Statusen är beta.
      </p>
      {varning && (
        <p class="fel" role="alert">
          {varning}
        </p>
      )}
      <form
        class="formular"
        aria-label="Homeys adress"
        onSubmit={(e) => {
          e.preventDefault();
          void props.spara("homey", () =>
            api.andraPlats(plats, { homey: { adress: adress.trim() || null } }),
          );
        }}
      >
        <Homeyadressfalt adress={adress} forslag={h.forslag} satt={setAdress} />
        <div class="knapprad">
          <button type="submit" class="knapp-andra" disabled={!adressOk}>
            Spara Homey
          </button>
        </div>
      </form>
      <form
        class="formular"
        aria-label="Nyckeln för Homey"
        onSubmit={(e) => {
          e.preventDefault();
          void sparaNyckel();
        }}
      >
        {homeylage(h).map((t) => (
          <p key={t} class="liten">
            {t}
          </p>
        ))}
        {h.lada ? (
          <>
            <Homeynyckelfalt varde={nyckel} satt={setNyckel} />
            <div class="knapprad">
              <button
                type="submit"
                class="knapp-andra"
                disabled={!HOMEY_NYCKEL.test(nyckel.trim())}
              >
                Försegla och spara nyckeln
              </button>
            </div>
          </>
        ) : (
          <p class="liten">
            Navet kan inte ta emot nyckeln än: det behöver navets version från b17.
          </p>
        )}
      </form>
      {props.beskedrad}
    </Kort>
  );
}
